import { useSyncExternalStore } from 'react';
import { campaignKey, createDataLayer } from '@signalvote/data';
import { useCampaignStore } from '@/stores';

function subscribeOnline(onChange: () => void): () => void {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
}

function useOnline(): boolean {
  return useSyncExternalStore(subscribeOnline, () => navigator.onLine, () => true);
}

function useCampaignId(): string | null {
  return useCampaignStore((s) => s.selectedCampaign?.id ?? null);
}

/** The web's `defineQuery` and `defineWrite`: keys and fetches scoped to the selected campaign, idle until one is selected. */
export const { defineQuery, defineInfiniteQuery, defineWrite } = createDataLayer({ useCampaignId, useOnline, requireCampaign: true });
export { edit, every, failureOf } from '@signalvote/data';

/**
 * The cache key a defined query holds for the selected campaign, for the invalidations and cache
 * writes outside a render. It reads the same store field `useCampaignId` does.
 */
export function selectedCampaignKey(key: readonly unknown[]): unknown[] {
  return campaignKey(useCampaignStore.getState().selectedCampaign?.id, key);
}

/**
 * The request headers a definition's fetch sends. `authenticatedFetch` lets a caller's headers
 * win over the store's, so the request asks for the campaign the cache key names even when the
 * store moved on mid-flight.
 */
export function campaignHeaders(campaignId: string | null): Record<string, string> {
  return campaignId ? { 'X-Campaign-Id': campaignId } : {};
}

/**
 * For a definition whose fetch goes through `@signalvote/api-client`, which sends the store's
 * campaign rather than a per-call one: refuses the request when the store has moved off the
 * campaign the cache key names, so one campaign's answer is never cached under another's key.
 */
export function requireSelectedCampaign(campaignId: string | null): void {
  if (useCampaignStore.getState().selectedCampaign?.id !== campaignId) {
    throw new Error('The selected campaign changed before this read was sent');
  }
}
